import omit from 'lodash/omit';
import { ApiMethod, deleteRequest, get, patch, post, put } from 'services/api';
import { isEmptyString } from 'utils/stringHelpers';

/**
 * Convert a comma-separated string into multiple key: value pairs for API URLs
 * Example: 'a, b, c' => key=a&key=b&key=c || original string if no comma is present
 */
export const convertListToValues = (data = '', key) => {
  const isList = Boolean(data.includes(','));
  if (!isList) {
    return data;
  }

  // remove spaces and convert string to string[]
  const list = data.replaceAll(' ', '').split(',');
  // convert string[] back to string while adding a key (ie. ['a', 'b'] => key=a&key=b)
  return list.reduce((acc, curr, index) => {
    return index === 0 ? acc.concat(curr) : acc.concat(`&${key}=${curr}`);
  }, '');
};

/**
 * parameterize
 * Turns an object into a parameter string.
 * ```
 * parameterize(data)
 * ```
 * @param { Object } data: the data that needs to be parameterized.
 * @returns { string } the parameterized string.
 */
export const parameterize = (data) =>
  Object.keys(data).reduce((stringified, key, i) => {
    const s = i === 0 ? `${stringified}` : `${stringified}&`;

    // Convert array to series of key/value pairs (ie. key=val1&key=val2)
    // otherwise pass through the string (ie. key=value or key=val1,val2)
    const value = Array.isArray(data[key])
      ? convertListToValues(data[key].toString(), key)
      : data[key].toString();

    // if empty value, don't add key to URL (ie. '&key=')
    if (isEmptyString(value)) {
      return stringified;
    }

    return `${s}${key}=${value}`;
  }, '');

/**
 * sanitizeData
 * Takes an object of parameters and returns one
 * that includes all the paramaters
 * that do not belong in the route path.
 * e.g.: an object of params associated with
 * the route "/reindex/:index/:newIndex"
 * would return an object without index or newIndex keys.
 * ```
 * sanitizeData(data)
 * ```
 * @param { String } route: the route associated with the parameters
 * @param { Object } data: the data that needs to be parameterized.
 * @returns { Object } the sanitized object.
 */
export const sanitizeData = (route, data) => {
  const parts = route.split('/');
  let sanitizedData = Object.assign({}, data);
  parts.forEach((part) => {
    if (part[0] === ':') {
      const param = part.slice(1);
      sanitizedData = omit(sanitizedData, param);
    }
  });
  return sanitizedData;
};

/**
 * sanitizeUrl
 * Takes a route with variable parameters and returns one
 * that includes all the paramaters in the path.
 * e.g.: an object of params:
 * { index: 1, newIndex: 2 }
 * associated with the route "/reindex/:index/:newIndex"
 * would return the route "/reindex/1/2"
 * ```
 * sanitizeUrl(data)
 * ```
 * @param { String } route: the route associated with the parameters
 * @param { Object } data: the data that needs to be parameterized.
 * @returns { String } the sanitized route.
 */
export const sanitizeUrl = (route, data) => {
  const parts = route.split('/');
  const sanitizedRoute = parts.reduce((newRoute, part, i) => {
    const path = part[0] === ':' ? data[part.slice(1)] : part;
    return i !== parts.length - 1 ? `${newRoute}${path}/` : `${newRoute}${path}`;
  }, '');
  return sanitizedRoute;
};

/**
 * requestAPI
 * Posts the request
 * ```
 * requestAPI(request)
 * ```
 * @param { Object } request: made up of the
 * route that's being posted to, the necessary cookies,
 * and any params (data) provided.
 * @returns { Promise } Posted results.
 */
export const requestAPI = (request) => {
  const { route, cookies, data, method = ApiMethod.post } = request;
  const xsrf = cookies.find((cookie) => cookie.key === 'XSRF-TOKEN');
  const params = parameterize(sanitizeData(route, data));
  const url = `${sanitizeUrl(route, data)}${params && `?${params}`}`;
  const options = {
    withCredentials: true,
    xsrfCookieName: xsrf.value,
    headers: {
      'x-XSRF-TOKEN': xsrf.value,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  switch (method) {
    case ApiMethod.delete:
      return deleteRequest(url, options);
    case ApiMethod.get:
      return get(url, options);
    case ApiMethod.patch:
      return patch(url, options);
    case ApiMethod.put:
      return put(url, options);
    case ApiMethod.post:
    default:
      return post(url, options);
  }
};
